import { useState, useMemo, useRef, useEffect } from 'react';

import { PopoverOptions } from './types';

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useClick,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';

export function usePopover({
  event = 'click',
  initialOpen = false,
  placement = 'bottom',
  open: controlledOpen,
  showArrow = true,
  onOpenChange: setControlledOpen,
  onOpenChangeCallback,
  withBoxShadow = false,
  withStyling = true,
  withPadding = true,
  dismisssOnOutsidePress = true,
  maxWidth,
}: PopoverOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = useRef(null);

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    enabled: event === 'hover',
    move: false,
  });

  const focus = useFocus(context, {
    enabled: event === 'hover',
  });

  const click = useClick(context, {
    enabled: event === 'click',
  });

  const dismiss = useDismiss(context, { outsidePress: dismisssOnOutsidePress });
  const role = useRole(context);

  const interactions = useInteractions([click, hover, focus, dismiss, role]);

  useEffect(() => {
    onOpenChangeCallback && onOpenChangeCallback(open);
  }, [open]);

  return useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      showArrow,
      targetRef: data.refs.setReference,
      withBoxShadow,
      withStyling,
      withPadding,
      ...interactions,
      ...data,
      maxWidth,
    }),
    [open, setOpen, interactions, data]
  );
}
